import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle, Store, Building2 } from 'lucide-react';

// Categorie tipologia attività
const BUSINESS_TYPES = [
  { value: '', label: 'Seleziona tipologia...' },
  { value: 'ristorante', label: 'Ristorante / Pizzeria' },
  { value: 'bar_caffetteria', label: 'Bar / Caffetteria' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'b&b', label: 'B&B / Affittacamere' },
  { value: 'agriturismo', label: 'Agriturismo' },
  { value: 'stabilimento_balneare', label: 'Stabilimento Balneare' },
  { value: 'negozio', label: 'Negozio / Retail' },
  { value: 'salone_bellezza', label: 'Salone di Bellezza / Barbiere' },
  { value: 'centro_estetico', label: 'Centro Estetico / SPA' },
  { value: 'palestra', label: 'Palestra / Centro Sportivo' },
  { value: 'studio_professionale', label: 'Studio Professionale' },
  { value: 'studio_medico', label: 'Studio Medico / Dentistico' },
  { value: 'agenzia', label: 'Agenzia (immobiliare, viaggi, ecc.)' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'artigiano', label: 'Artigiano / Laboratorio' },
  { value: 'altro', label: 'Altro' },
];
import { useAuth } from '../hooks/useAuth';

// Mappa servizi da query param a valore backend
const SERVICE_MAP = {
  'review': 'review',
  'smart-review': 'review',
  'page': 'page',
  'smart-page': 'page',
  'menu': 'menu',
  'menu_digitale': 'menu',
  'chat_ai': 'chat_ai',
  'chatai': 'chat_ai',
  'display': 'display',
  'display_suite': 'display',
  'agent_ai': 'agent_ai',
  'agentai': 'agent_ai',
  'connect': 'connect'
};

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  // Estrai UTM params, servizio e flag Hub dalla URL.
  // ?hub=monthly|yearly arriva dalla landing combo_estate26/hub: attiva Page+Review+Menu in bundle.
  const urlParams = useMemo(() => {
    const serviceParam = searchParams.get('service');
    const hubParam = searchParams.get('hub');
    const isHub = hubParam === 'monthly' || hubParam === 'yearly';
    return {
      // Su registrazione Hub il backend attiva tutti e 3 i trial; lasciamo
      // requestedService al default per non confondere il branch single-service.
      requestedService: SERVICE_MAP[serviceParam] || 'review',
      isHub,
      hubBillingCycle: isHub ? hubParam : null,
      utmSource: searchParams.get('utm_source'),
      utmMedium: searchParams.get('utm_medium'),
      utmCampaign: searchParams.get('utm_campaign'),
      utmContent: searchParams.get('utm_content'),
      referralCode: searchParams.get('ref') || searchParams.get('referral')
    };
  }, [searchParams]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    activityName: '',
    businessType: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Le password non coincidono');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      setLoading(false);
      return;
    }

    // Validate activity name if provided
    if (formData.activityName && formData.activityName.length < 3) {
      setError('Il nome attività deve essere di almeno 3 caratteri');
      setLoading(false);
      return;
    }

    const result = await register({
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      fullName: `${formData.firstName} ${formData.lastName}`.trim(),
      activityName: formData.activityName || undefined,
      businessType: formData.businessType || undefined,
      ...urlParams
    });

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Registrazione completata!
            </h2>
            <p className="text-gray-600 mb-6">
              Controlla la tua email per confermare l'account.
            </p>
            <Link to="/login" className="btn-primary inline-block px-8">
              Vai al Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/icons/doidSuiteVerticale.png" alt="doID Suite" className="mx-auto mb-4 object-contain" style={{ width: '200px' }} />
          <p className="text-gray-500 mt-2">Crea il tuo account</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="label">
                  Nome
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="input"
                  placeholder="Mario"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="label">
                  Cognome
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="input"
                  placeholder="Rossi"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="nome@azienda.it"
              />
            </div>

            <div>
              <label htmlFor="activityName" className="label">
                Nome della tua attività
              </label>
              <div className="relative">
                <input
                  id="activityName"
                  name="activityName"
                  type="text"
                  autoComplete="organization"
                  value={formData.activityName}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="Es. Ristorante Da Mario"
                />
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Il nome dell'attività per cui attiverai i servizi doID
              </p>
            </div>

            <div>
              <label htmlFor="businessType" className="label">
                Tipologia di attività
              </label>
              <div className="relative">
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  className="input pl-10 appearance-none"
                >
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input pr-10"
                  placeholder="Minimo 8 caratteri"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Conferma Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input"
                placeholder="Ripeti la password"
              />
            </div>

            <div className="flex items-start">
              <input
                id="terms"
                type="checkbox"
                required
                className="w-4 h-4 mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                Accetto i{' '}
                <a href="https://doid.it/termini-di-servizio/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  Termini di Servizio
                </a>{' '}
                e la{' '}
                <a href="https://www.iubenda.com/privacy-policy/86826482" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  Privacy Policy
                </a>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Registrati'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Hai già un account?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-700"
              >
                Accedi
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-400">
          © {new Date().getFullYear()} doID. Tutti i diritti riservati.
        </p>
      </div>
    </div>
  );
}
